/**
 * ProvinceRoleReport — which assemblies still have an essential office empty.
 * A Super Admin decides which offices count as essential, right here.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, CircleSlash, Settings2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
};

export const ProvinceRoleReport = ({ unitId, canEditRequirements }) => {
  const qc = useQueryClient();
  const [tuning, setTuning] = useState(false);
  const [picked, setPicked] = useState(null);

  const { data: reqs } = useQuery({
    queryKey: ['role-requirements'],
    queryFn: () => call('/api/province/role-requirements'),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ['role-report', unitId],
    queryFn: () => call(`/api/province/${unitId}/role-report`),
    enabled: !!unitId,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => call('/api/province/role-requirements', {
      method: 'PUT', body: JSON.stringify({ roles: picked }),
    }),
    onSuccess: () => {
      toast.success('Essential offices updated');
      qc.invalidateQueries({ queryKey: ['role-requirements'] });
      qc.invalidateQueries({ queryKey: ['role-report'] });
      setTuning(false);
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="py-16 text-center text-slate-400" data-testid="role-report-loading">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }
  if (error) {
    return <p className="py-16 text-center text-sm text-rose-600" data-testid="role-report-error">{error.message}</p>;
  }

  const s = data.summary;
  const current = picked ?? (reqs?.roles || []);

  return (
    <div className="space-y-4" data-testid="province-role-report">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">
                {s.complete} of {s.assemblies} assemblies fully staffed
              </CardTitle>
              <CardDescription>
                Essential offices: {data.required.map((r) => r.label).join(', ')}
              </CardDescription>
            </div>
            {canEditRequirements && (
              <Button variant="outline" size="sm"
                      onClick={() => { setPicked(reqs?.roles || []); setTuning(!tuning); }}
                      data-testid="role-report-configure">
                <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Choose the essentials
              </Button>
            )}
          </div>
        </CardHeader>
        {tuning && canEditRequirements && (
          <CardContent className="border-t pt-3 space-y-3" data-testid="role-report-configure-panel">
            <div className="grid sm:grid-cols-3 gap-2">
              {(reqs?.options || []).map((o) => (
                <label key={o.key} className="flex items-center gap-2 rounded-md border border-slate-200 p-2 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={current.includes(o.key)}
                            onCheckedChange={() => setPicked(current.includes(o.key)
                              ? current.filter((k) => k !== o.key)
                              : [...current, o.key])}
                            data-testid={`role-req-${o.key}`} />
                  <span className="text-sm text-slate-700">{o.label}</span>
                </label>
              ))}
            </div>
            <Button size="sm" disabled={save.isPending || current.length === 0}
                    onClick={() => save.mutate()} data-testid="role-req-save">
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
            </Button>
          </CardContent>
        )}
      </Card>

      <div className="flex flex-wrap gap-2" data-testid="role-report-gaps">
        {data.required.map((r) => (
          <Badge key={r.key} variant="outline"
                 className={s.gaps_by_role[r.key]
                   ? 'border-rose-200 text-rose-700 bg-rose-50'
                   : 'border-emerald-200 text-emerald-700 bg-emerald-50'}
                 data-testid={`role-gap-${r.key}`}>
            {r.label}: {s.gaps_by_role[r.key] ? `${s.gaps_by_role[r.key]} missing` : 'all filled'}
          </Badge>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Assembly</th>
                {data.required.map((r) => (
                  <th key={r.key} className="text-left py-2 px-3">{r.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.assemblies.map((a) => (
                <tr key={a.id} className={`border-b last:border-0 ${a.complete ? '' : 'bg-rose-50/40'}`}
                    data-testid={`role-row-${a.id}`}>
                  <td className="py-2 px-3">
                    <Link to={`/admin/churches/${a.id}`} className="font-medium text-slate-800 hover:text-emerald-700">
                      {a.name}
                    </Link>
                    {a.city && <span className="text-[10px] text-slate-400 ml-1">{a.city}</span>}
                  </td>
                  {data.required.map((r) => (
                    <td key={r.key} className="py-2 px-3" data-testid={`role-cell-${a.id}-${r.key}`}>
                      {a.filled[r.key] ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {a.filled[r.key].who || a.filled[r.key].matched}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-600">
                          <CircleSlash className="h-3.5 w-3.5" /> vacant
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProvinceRoleReport;
